// File: src/components/event/EventStaffingOverview.jsx
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/axios";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { formatCurrency } from "@/utils/formatting";
import { Loader2, Users, Briefcase, Clock3, PoundSterling, CheckCircle2, AlertTriangle } from "lucide-react";

const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const safeText = (v, d = "—") => {
  const s = v == null ? "" : String(v).trim();
  return s ? s : d;
};

const formatHours = (v) => {
  const n = toNum(v, 0);
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

function normalizePayload(data) {
  const staffing = data?.staffing || {};

  return {
    event: data?.event || {},
    rotaAssignments: Array.isArray(staffing?.rota_assignments) ? staffing.rota_assignments : [],
    forecastCostLines: Array.isArray(staffing?.forecast_cost_lines) ? staffing.forecast_cost_lines : [],
    actualCostLines: Array.isArray(staffing?.actual_cost_lines) ? staffing.actual_cost_lines : [],
    byCostCentre: Array.isArray(staffing?.by_cost_centre) ? staffing.by_cost_centre : [],
    totals: staffing?.totals || {},
    meta: data?.meta || {},
  };
}

async function getEventStaffingWithFallbacks(eventId) {
  const tries = [
    `/event/${eventId}/staffing`,
    `/event/events/${eventId}/staffing`,
    `/events/${eventId}/staffing`,
  ];

  let lastErr = null;

  for (const url of tries) {
    try {
      const res = await api.get(url);
      return normalizePayload(res.data);
    } catch (err) {
      lastErr = err;
      if (err?.response?.status === 404) continue;
      throw err;
    }
  }

  throw lastErr;
}

function StatCard({ title, value, subtitle, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">{title}</div>
          <div className="mt-2 text-2xl font-bold text-slate-950">{value}</div>
          {subtitle ? <div className="mt-1 text-xs text-slate-500">{subtitle}</div> : null}
        </div>
        {Icon ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-3 text-slate-700">
            <Icon className="h-4 w-4" />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function EventStaffingOverview({ eventId }) {
  const { canViewEventStaffing } = useUserPermissions();

  const [payload, setPayload] = useState({
    event: {},
    rotaAssignments: [],
    forecastCostLines: [],
    actualCostLines: [],
    byCostCentre: [],
    totals: {},
    meta: {},
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!eventId || !canViewEventStaffing) return;

    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const data = await getEventStaffingWithFallbacks(eventId);
        if (cancelled) return;

        setPayload(data);
      } catch (err) {
        if (cancelled) return;

        console.error("Failed to load staffing overview:", err);
        setError(
          err?.response?.data?.error ||
            err?.response?.data?.detail ||
            err?.message ||
            "Failed to load staffing overview"
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [eventId, canViewEventStaffing]);

  const { rotaAssignments, forecastCostLines, actualCostLines, byCostCentre, totals, meta } = payload;

  const hasAnyData = useMemo(() => {
    return (
      rotaAssignments.length > 0 ||
      forecastCostLines.length > 0 ||
      actualCostLines.length > 0 ||
      byCostCentre.length > 0
    );
  }, [rotaAssignments, forecastCostLines, actualCostLines, byCostCentre]);

  if (!canViewEventStaffing) {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-rose-700 font-semibold">
        You do not have permission to view this staffing overview.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-2 text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading staffing overview...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 shadow-sm">
        <div className="font-semibold text-rose-700">Failed to load staffing overview</div>
        <div className="mt-1 text-sm text-rose-600">{error}</div>
      </div>
    );
  }

  if (!hasAnyData) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="text-slate-700 font-semibold">No staffing data found for this event.</div>
        <div className="mt-1 text-sm text-slate-500">
          No rota assignments or staffing cost lines were returned yet.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-4">
          <h2 className="text-xl font-bold text-slate-950">Event Staffing Overview</h2>
          <p className="mt-1 text-sm text-slate-600">
            Staffing assignments, forecast staffing costs, actual staffing costs, and cost-centre variance.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
          <StatCard
            title="Assigned Staff"
            value={toNum(totals?.rota_assigned_count, 0)}
            subtitle="Rota assignments"
            icon={Users}
          />
          <StatCard
            title="Rota Hours"
            value={formatHours(totals?.rota_hours)}
            subtitle="Total scheduled hours"
            icon={Clock3}
          />
          <StatCard
            title="Rota Cost"
            value={formatCurrency(toNum(totals?.rota_cost, 0))}
            subtitle="Scheduled staffing cost"
            icon={Briefcase}
          />
          <StatCard
            title="Forecast Staffing"
            value={formatCurrency(toNum(totals?.forecast_staffing_cost, 0))}
            subtitle="Forecast staffing cost"
            icon={PoundSterling}
          />
          <StatCard
            title="Actual Staffing"
            value={formatCurrency(toNum(totals?.actual_staffing_cost, 0))}
            subtitle="Actual staffing cost"
            icon={CheckCircle2}
          />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-2">
          <StatCard
            title="Forecast vs Actual"
            value={formatCurrency(toNum(totals?.forecast_vs_actual_variance, 0))}
            subtitle="Actual minus forecast"
            icon={AlertTriangle}
          />
          <StatCard
            title="Rota vs Actual"
            value={formatCurrency(toNum(totals?.rota_vs_actual_variance, 0))}
            subtitle="Actual minus rota"
            icon={AlertTriangle}
          />
        </div>
      </section>

      {rotaAssignments.length > 0 ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-lg font-bold text-slate-950">Rota Assignments</h3>
          <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-700">
                <tr>
                  <th className="p-3 text-left">Name</th>
                  <th className="p-3 text-left">Role</th>
                  <th className="p-3 text-left">Start</th>
                  <th className="p-3 text-left">End</th>
                  <th className="p-3 text-right">Hours</th>
                  <th className="p-3 text-right">Rate/hr</th>
                  <th className="p-3 text-right">Total Cost</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Cost Centre</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {rotaAssignments.map((staff, idx) => (
                  <tr key={`${staff.staff_id || "staff"}-${idx}`} className="text-slate-800">
                    <td className="p-3 font-medium">{safeText(staff.full_name)}</td>
                    <td className="p-3">{safeText(staff.role)}</td>
                    <td className="p-3">{safeText(staff.start_time)}</td>
                    <td className="p-3">{safeText(staff.end_time)}</td>
                    <td className="p-3 text-right">{formatHours(staff.hours)}</td>
                    <td className="p-3 text-right">{formatCurrency(toNum(staff.hourly_rate, 0))}</td>
                    <td className="p-3 text-right">{formatCurrency(toNum(staff.total_cost, 0))}</td>
                    <td className="p-3">{safeText(staff.status)}</td>
                    <td className="p-3">{safeText(staff.cost_centre?.name)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-lg font-bold text-slate-950">Forecast Cost Lines</h3>
          {forecastCostLines.length === 0 ? (
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              No forecast staffing cost lines found.
            </div>
          ) : (
            <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-700">
                  <tr>
                    <th className="p-3 text-left">Cost Centre</th>
                    <th className="p-3 text-right">Amount</th>
                    <th className="p-3 text-left">Notes</th>
                    <th className="p-3 text-left">Submitted</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {forecastCostLines.map((row, idx) => (
                    <tr key={`forecast-${idx}`} className="text-slate-800">
                      <td className="p-3">{safeText(row.cost_centre?.name)}</td>
                      <td className="p-3 text-right">{formatCurrency(toNum(row.amount, 0))}</td>
                      <td className="p-3">{safeText(row.notes)}</td>
                      <td className="p-3">{safeText(row.submitted_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-lg font-bold text-slate-950">Actual Cost Lines</h3>
          {actualCostLines.length === 0 ? (
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              No actual staffing cost lines found.
            </div>
          ) : (
            <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-700">
                  <tr>
                    <th className="p-3 text-left">Cost Centre</th>
                    <th className="p-3 text-right">Amount</th>
                    <th className="p-3 text-left">Notes</th>
                    <th className="p-3 text-left">Submitted</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {actualCostLines.map((row, idx) => (
                    <tr key={`actual-${idx}`} className="text-slate-800">
                      <td className="p-3">{safeText(row.cost_centre?.name)}</td>
                      <td className="p-3 text-right">{formatCurrency(toNum(row.amount, 0))}</td>
                      <td className="p-3">{safeText(row.notes)}</td>
                      <td className="p-3">{safeText(row.submitted_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {byCostCentre.length > 0 ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-lg font-bold text-slate-950">Cost Centre Comparison</h3>
          <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-700">
                <tr>
                  <th className="p-3 text-left">Cost Centre</th>
                  <th className="p-3 text-right">Rota Cost</th>
                  <th className="p-3 text-right">Forecast Cost</th>
                  <th className="p-3 text-right">Actual Cost</th>
                  <th className="p-3 text-right">Forecast vs Actual</th>
                  <th className="p-3 text-right">Rota vs Actual</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {byCostCentre.map((row, idx) => (
                  <tr key={`cc-${row.cost_centre_id || idx}`} className="text-slate-800">
                    <td className="p-3">{safeText(row.name)}</td>
                    <td className="p-3 text-right">{formatCurrency(toNum(row.rota_cost, 0))}</td>
                    <td className="p-3 text-right">{formatCurrency(toNum(row.forecast_cost, 0))}</td>
                    <td className="p-3 text-right">{formatCurrency(toNum(row.actual_cost, 0))}</td>
                    <td className="p-3 text-right">{formatCurrency(toNum(row.forecast_vs_actual_variance, 0))}</td>
                    <td className="p-3 text-right">{formatCurrency(toNum(row.rota_vs_actual_variance, 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-xs text-slate-500">
            Forecast table available: {meta?.forecast_table_available ? "Yes" : "No"} • Actual table available:{" "}
            {meta?.actual_table_available ? "Yes" : "No"}
          </div>
        </section>
      ) : null}
    </div>
  );
}